import React, { useState, useEffect } from "react";
import { profileData, linksData, videoBackground } from "../data/mockData";
import { showPreferences } from "../lib/cookieConsent";

const LinkButton = ({ link }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <a
      href={link.url}
      target="_blank"
      rel="noopener noreferrer"
      className="group relative block w-full"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{ textDecoration: "none" }}
    >
      <div
        className="relative flex min-h-[64px] w-full items-center rounded-lg overflow-hidden"
        style={{
          backgroundColor: isHovered
            ? "rgba(255, 255, 255, 0.95)"
            : "rgba(255, 255, 255, 0.85)",
          transform: isHovered ? "scale(1.02)" : "scale(1)",
          boxShadow: isHovered
            ? "0 4px 16px rgba(0,0,0,0.18)"
            : "0 1px 3px rgba(0,0,0,0.08)",
          transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        {/* Thumbnail */}
        <div className="absolute left-2 top-2 bottom-2 flex h-12 w-12 items-center justify-center overflow-hidden rounded-md flex-shrink-0">
          {link.thumbnail ? (
            <img
              src={link.thumbnail}
              alt={link.title}
              className={`h-full w-full ${link.thumbnailFit === "contain" ? "object-contain" : "object-cover"}`}
              loading="eager"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center rounded-md bg-gray-200 text-[10px] text-gray-500">
              —
            </div>
          )}
        </div>

        {/* Title */}
        <div className="w-full px-16 py-3 text-center">
          <span className="text-[14px] font-medium leading-[1.2] text-gray-900 sm:text-[16px]">
            {link.title}
          </span>
        </div>

        {/* Share dots button */}
        <button
          className="absolute right-3 top-1/2 -translate-y-1/2 flex h-6 w-6 items-center justify-center rounded-xl text-gray-500 opacity-40 hover:opacity-80"
          style={{ transition: "opacity 0.2s" }}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <ThreeDotsIcon />
        </button>
      </div>
    </a>
  );
};

const ThreeDotsIcon = () => (
  <svg
    width="3"
    height="11"
    viewBox="0 0 3 11"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      className="fill-current"
      d="M1.33333 10.6667C0.966667 10.6667 0.652778 10.5361 0.391667 10.275C0.130556 10.0139 0 9.7 0 9.33333C0 8.96667 0.130556 8.65278 0.391667 8.39167C0.652778 8.13056 0.966667 8 1.33333 8C1.7 8 2.01389 8.13056 2.275 8.39167C2.53611 8.65278 2.66667 8.96667 2.66667 9.33333C2.66667 9.7 2.53611 10.0139 2.275 10.275C2.01389 10.5361 1.7 10.6667 1.33333 10.6667ZM1.33333 6.66667C0.966667 6.66667 0.652778 6.53611 0.391667 6.275C0.130556 6.01389 0 5.7 0 5.33333C0 4.96667 0.130556 4.65278 0.391667 4.39167C0.652778 4.13056 0.966667 4 1.33333 4C1.7 4 2.01389 4.13056 2.275 4.39167C2.53611 4.65278 2.66667 4.96667 2.66667 5.33333C2.66667 5.7 2.53611 6.01389 2.275 6.275C2.01389 6.53611 1.7 6.66667 1.33333 6.66667ZM1.33333 2.66667C0.966667 2.66667 0.652778 2.53611 0.391667 2.275C0.130556 2.01389 0 1.7 0 1.33333C0 0.966667 0.130556 0.652778 0.391667 0.391667C0.652778 0.130556 0.966667 0 1.33333 0C1.7 0 2.01389 0.130556 2.275 0.391667C2.53611 0.652778 2.66667 0.966667 2.66667 1.33333C2.66667 1.7 2.53611 2.01389 2.275 2.275C2.01389 2.53611 1.7 2.66667 1.33333 2.66667Z"
    />
  </svg>
);

const YT_BG_PLAYER_ID = "yt-background-player";

const LinkTreePage = () => {
  const [shareTooltip, setShareTooltip] = useState(false);

  useEffect(() => {
    const { videoId, startSeconds, endSeconds } = videoBackground;
    let cancelled = false;
    let player = null;
    let intervalId = null;

    const loopSegment = (p) => {
      if (cancelled || !p) return;
      p.seekTo(startSeconds, true);
      p.playVideo();
    };

    const onStateChange = (event) => {
      if (cancelled || event.data !== window.YT.PlayerState.ENDED) return;
      loopSegment(event.target);
    };

    const startTimeGuard = (p) => {
      intervalId = window.setInterval(() => {
        if (cancelled || !p || typeof p.getCurrentTime !== "function") return;
        try {
          if (p.getCurrentTime() >= endSeconds - 0.2) {
            loopSegment(p);
          }
        } catch {
          /* ignore */
        }
      }, 300);
    };

    const createPlayer = () => {
      if (cancelled || !window.YT?.Player) return;
      player = new window.YT.Player(YT_BG_PLAYER_ID, {
        videoId,
        playerVars: {
          autoplay: 1,
          mute: 1,
          controls: 0,
          modestbranding: 1,
          playsinline: 1,
          rel: 0,
          start: startSeconds,
          end: endSeconds,
          disablekb: 1,
          fs: 0,
          iv_load_policy: 3,
        },
        events: {
          onReady: (e) => {
            if (cancelled) return;
            e.target.mute();
            e.target.playVideo();
            startTimeGuard(e.target);
          },
          onStateChange: onStateChange,
        },
      });
    };

    const loadApi = () =>
      new Promise((resolve) => {
        if (window.YT?.Player) {
          resolve();
          return;
        }
        if (!document.querySelector('script[src="https://www.youtube.com/iframe_api"]')) {
          const tag = document.createElement("script");
          tag.src = "https://www.youtube.com/iframe_api";
          const first = document.getElementsByTagName("script")[0];
          first.parentNode.insertBefore(tag, first);
        }
        const prev = window.onYouTubeIframeAPIReady;
        window.onYouTubeIframeAPIReady = function () {
          if (typeof prev === "function") prev();
          resolve();
        };
      });

    loadApi().then(() => {
      if (cancelled) return;
      createPlayer();
    });

    return () => {
      cancelled = true;
      if (intervalId != null) {
        window.clearInterval(intervalId);
        intervalId = null;
      }
      if (player && typeof player.destroy === "function") {
        try {
          player.destroy();
        } catch {
          /* ignore */
        }
        player = null;
      }
    };
  }, []);

  const handleShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: profileData.name,
          url: window.location.href,
        });
      } else {
        await navigator.clipboard.writeText(window.location.href);
        setShareTooltip(true);
        setTimeout(() => setShareTooltip(false), 2000);
      }
    } catch (err) {
      // Silently handle
    }
  };

  return (
    <div className="relative min-h-screen w-full overflow-hidden" style={{ fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}>
      {/* YouTube Video Background */}
      <div className="video-bg fixed inset-0 z-0 overflow-hidden">
        <div className="absolute inset-0 bg-black">
          <div className="video-bg-cover">
            <div
              id={YT_BG_PLAYER_ID}
              title="Background Video"
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                pointerEvents: "none",
              }}
            />
          </div>
        </div>
        {/* Subtle dark overlay for text readability */}
        <div className="absolute inset-0" style={{ backgroundColor: "rgba(0, 0, 0, 0.15)" }} />
      </div>

      {/* Top Bar removed as per user request */}

      {/* Main Content */}
      <div className="relative z-10 mx-auto flex min-h-screen max-w-[680px] flex-col px-5 sm:px-10">
        {/* Profile Header */}
        <div className="flex flex-col items-center pt-24 pb-2">
          {/* Avatar sur disque bleu marine (#0B1F3F) pour contraste sur toute vidéo */}
          <div
            className="mb-4 flex shrink-0 items-center justify-center overflow-hidden rounded-full"
            style={{
              width: "140px",
              height: "140px",
              backgroundColor: "#0B1F3F",
              padding: "14px",
              boxShadow:
                "0 4px 18px rgba(0, 0, 0, 0.45), inset 0 1px 0 rgba(255, 255, 255, 0.06)",
            }}
          >
            <img
              src={profileData.avatar}
              alt="Profile"
              className="h-full w-full object-contain"
              style={{ borderRadius: "50%" }}
            />
          </div>

          {/* Name */}
          <h1
            className="text-center font-semibold text-white"
            style={{
              fontSize: "1.5rem",
              textShadow: "0 1px 6px rgba(0,0,0,0.35)",
              overflowWrap: "anywhere",
              lineHeight: 1.3,
            }}
          >
            {profileData.name}
          </h1>

          {/* Bio */}
          <h2
            className="mt-1 text-center text-white/90"
            style={{
              fontSize: "0.875rem",
              textShadow: "0 1px 4px rgba(0,0,0,0.3)",
              overflowWrap: "anywhere",
              maxWidth: "80%",
              lineHeight: 1.4,
            }}
          >
            {profileData.bio}
          </h2>
        </div>

        {/* Links */}
        <div className="mt-8 flex flex-col gap-4 pb-8">
          {linksData.map((link) => (
            <LinkButton key={link.id} link={link} />
          ))}
        </div>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Footer */}
        <div className="flex flex-col items-center gap-2 pb-16 pt-12">
          <div className="flex items-center gap-0 text-white/60" style={{ fontSize: "10px", lineHeight: "1.5" }}>
            <button
              type="button"
              className="hover:underline cursor-pointer bg-transparent border-0 p-0 font-inherit text-inherit"
              style={{ fontSize: "10px", lineHeight: "1.5" }}
              onClick={() => showPreferences()}
            >
              Préférences cookies
            </button>
            <span className="px-1">&bull;</span>
            <a
              href="https://wownautic.com/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:underline"
              style={{ fontSize: "10px", lineHeight: "1.5", color: "inherit" }}
            >
              Privacy
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LinkTreePage;
